import React, { useMemo, useRef, useEffect, useState, useCallback } from "react";
import { CheckIcon } from "@heroicons/react/solid";
import { compactDuplicateLabel } from "../shared/FormPageLayout";
import "./Stepper.css";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const buildStepLabels = (template) => {
  const titleCounts = {};
  template.forEach((step) => {
    const key = (step.section_title || "").trim();
    titleCounts[key] = (titleCounts[key] || 0) + 1;
  });

  const titleSeen = {};
  return template.map((step) => {
    const title = (step.section_title || "Section").trim();
    if (titleCounts[title] > 1) {
      titleSeen[title] = (titleSeen[title] || 0) + 1;
      return compactDuplicateLabel(title, titleSeen[title]);
    }
    return title;
  });
};

/** On long forms, show labels for current/adjacent/end steps only. */
const shouldShowStepLabel = (stepIdx, currentIndex, total) => {
  if (total <= 5) return true;
  if (stepIdx === 0 || stepIdx === total - 1) return true;
  return Math.abs(stepIdx - currentIndex) <= 1;
};

const Stepper = ({
  template,
  onStepClickHandler,
  currentIndex = 0,
  actions = null,
}) => {
  const total = template.length;
  const progressPct = total > 0 ? ((currentIndex + 1) / total) * 100 : 0;
  const stepLabels = useMemo(() => buildStepLabels(template), [template]);
  const currentSection = template[currentIndex];
  const scrollRef = useRef(null);
  const isCompact = total > 5;
  const [scrollHints, setScrollHints] = useState({ left: false, right: false });

  const updateScrollHints = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    setScrollHints({
      left: container.scrollLeft > 4,
      right:
        container.scrollLeft + container.clientWidth < container.scrollWidth - 4,
    });
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return undefined;

    updateScrollHints();
    container.addEventListener("scroll", updateScrollHints, { passive: true });
    const observer = new ResizeObserver(updateScrollHints);
    observer.observe(container);

    return () => {
      container.removeEventListener("scroll", updateScrollHints);
      observer.disconnect();
    };
  }, [total, updateScrollHints]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const active = container.querySelector(`[data-step="${currentIndex}"]`);
    if (active) {
      const left =
        active.offsetLeft - (container.clientWidth - active.offsetWidth) / 2;
      container.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
    }
    updateScrollHints();
  }, [currentIndex, updateScrollHints]);

  return (
    <nav aria-label="Form progress" className="form-stepper-v2">
      <div className="form-stepper-v2-meta">
        <div>
          <p className="form-stepper-v2-eyebrow">
            Section {currentIndex + 1} of {total}
          </p>
          <p className="form-stepper-v2-current" title={currentSection?.section_title}>
            {currentSection?.section_title || "—"}
          </p>
        </div>
        <div className="form-stepper-v2-meta-end">
          {actions}
          {total > 1 && (
            <div className="form-stepper-v2-pct">
              <span className="form-stepper-v2-pct-value">
                {Math.round(progressPct)}%
              </span>
              <span className="form-stepper-v2-pct-label">through</span>
            </div>
          )}
        </div>
      </div>

      <div className="form-stepper-v2-track" aria-hidden="true">
        <div
          className="form-stepper-v2-track-fill"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      <div
        className={classNames(
          "form-stepper-v2-steps-wrap",
          scrollHints.left && "has-scroll-left",
          scrollHints.right && "has-scroll-right",
          isCompact && "is-compact"
        )}
      >
        <div ref={scrollRef} className="form-stepper-v2-steps custom-scroll">
          <ol className="form-stepper-v2-list">
            {template.map((step, stepIdx) => {
              const isComplete =
                step.status === "complete" || step.status === "active";
              const isCurrent =
                step.status === "current" || stepIdx === currentIndex;
              const isPast = stepIdx < currentIndex || isComplete;
              const showLabel = shouldShowStepLabel(stepIdx, currentIndex, total);
              const fullTitle = step.section_title || `Section ${stepIdx + 1}`;

              return (
                <li
                  key={stepIdx}
                  data-step={stepIdx}
                  className="form-stepper-v2-item"
                >
                  <button
                    type="button"
                    onClick={() => onStepClickHandler(stepIdx)}
                    className={classNames(
                      "form-stepper-v2-step",
                      isCurrent && "is-current",
                      isPast && !isCurrent && "is-complete",
                      !isPast && !isCurrent && "is-upcoming",
                      !showLabel && "is-compact-step"
                    )}
                    aria-current={isCurrent ? "step" : undefined}
                    aria-label={`${fullTitle}${isCurrent ? " (current)" : ""}`}
                    title={fullTitle}
                  >
                    <span className="form-stepper-v2-step-num">
                      {isPast && !isCurrent ? (
                        <CheckIcon className="w-3.5 h-3.5" aria-hidden="true" />
                      ) : (
                        stepIdx + 1
                      )}
                    </span>
                    {showLabel && (
                      <span className="form-stepper-v2-step-label">
                        {stepLabels[stepIdx]}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </nav>
  );
};

export default Stepper;
